import * as React from "react";
import {cx} from "emotion";

import "./map-section.scss";

import {UseState}         from "@Common/react/use-state";
import {cs}               from "@Common/react/chain-services";
import {chain}            from "@Common/utils/fs";
import {BubbleNoti}       from "@Common/ui-components/bubble-noti/bubble-noti";
import {isApiTrue}        from "@Common/logic/is-api-true";
import {ErrorHighlighter} from "@Common/ui-components/light/error-highlighter";

import {RadiusMapControl} from "./radius-map-control/radius-map-control";
import {DomSizeObserver}  from "dom-size-observer";

export const MapSection = ({addresses, loading, viewSize, view, next: rootNext}) => cs(
    ["hoveredMarker", (_, next) => UseState({
        next,
    })],
    ["noti", (_, next) => BubbleNoti({next})],
    ["barWidth", ({}, next) =>DomSizeObserver({
        translate: ({width}) => width,
        defaultValue: null,
        next
    })],
    ["messageWidth", ({}, next) => DomSizeObserver({
        translate: ({width}) => width,
        defaultValue: null,
        next
    })],
    ["eh", (_, next) => ErrorHighlighter({next})],
    ({barWidth, messageWidth, hoveredMarker, noti, eh}) => {
        const leftPos = barWidth?.value && messageWidth?.value ? (barWidth.value - messageWidth.value) / 2 : 43;
        
        const {done, total} = loading.value.loading ? loading.value.progress : {done: 0, total: 1};
        const loadingMessage = `${done} out of ${total} Addresses Plotted`;

        const plotBuildings = chain(
            Object.values(addresses.value ?? {}),
            (_) => _.flatMap(v=>v),
            (_) => _.filter(address => address?.pos && isApiTrue(address.pos.isMatched) && isApiTrue(address.pos.grouped)),
            (_) => _.reduce((acc, address) => ({
                ...acc,
                [address.pos.groupKey]: {
                    ...(acc[address.pos.groupKey] ?? address),
                    selected: acc[address.pos.groupKey]?.selected || isApiTrue(address.display_address),
                    type: "building",
                }
            }), {}),
            (_) => Object.values(_),
            (_) => _.filter(() => view.value === "addresses"),
        );
        const plotAddresses = chain(
            Object.values(addresses.value ?? {}),
            (_) => _.flatMap(v=>v),
            (_) => _.filter(address => address?.pos && isApiTrue(address.pos.isMatched) && !isApiTrue(address.pos.grouped)),
            (_) => _.map(address => ({
                ...address,
                selected: isApiTrue(address.display_address),
                type: "address",
            })),
            (_) => _.filter(() => view.value === "addresses"),
        );
        
        return rootNext({
            renderErrorNoti: ({msg, color}) => noti.show({
                message: msg,
                color,
                duration: 10000,
            }),
            highlightErrors: () => {
                eh.start();
            },
            render: () => (
                <div className="map-section-r2t">
                    {loading.value.loading && (
                        <div className="map-overlay">
                            <div className="right">
                                <div className={cx("address-progress-box", eh.css)}>
                                    <div className="progress">
                                        <div className="bar" ref={barWidth.ref}>
                                            <div className="fill" style={{width: `${Math.round(((done/total)*100))}%`}}>
                                                <span 
                                                    className="message"
                                                    xd-qa-name="Build Radius List Loading Message"
                                                    xd-qa-type="label"
                                                    style={{left: leftPos}}
                                                >
                                                    {loadingMessage}
                                                </span>
                                            </div>
                                            <span className="message" ref={messageWidth.ref} style={{left: leftPos}}>{loadingMessage}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {RadiusMapControl({
                        center: [37, -111],
                        hoveredMarker,
                        markers: [...plotAddresses, ...plotBuildings]?.map((marker) => ({
                            name: marker.address,
                            elementKey: toKey(marker),
                            isGrouped: isApiTrue(marker.pos.grouped),
                            coords: {
                                lat: marker.pos.lat,
                                lng: marker.pos.lng
                            },
                            opacity: (marker.selected ? .7 : .2) + (isApiTrue(marker.pos.grouped) ? .3 : 0),
                            onMouseOver: () => hoveredMarker.onChange({
                                key: toKey(marker),
                                label: marker.type === "building" ? "Units At:" : "Address",
                                name: marker.type === "building" ? marker.addressLabel : marker.address,
                                zip: marker.zip_code
                            }),
                            onMouseOut : () => hoveredMarker.onChange(null),
                        })),
                        viewSize,
                        totalAddresses: [...plotAddresses, ...plotBuildings].length,
                        selectedAddressCount: chain(
                            Object.values(addresses.value ?? {}),
                            (_) => _.flatMap(v=>v),
                            (_) => _.filter(a => isApiTrue(a.display_address)),
                            (_) => _.length,
                        ),
                    })}
                </div>
            )
        })
    },
);

const toKey = (address) => address.address + address.zip_code;